import React, { useState, useEffect } from "react";
import { retailerService } from "../../Services";
import ReactPaginate from "react-paginate";
import Loader from "../../Components/Loader";
import { Modal, Form, Badge, Button } from "react-bootstrap-v5";
import { toast } from "react-toastify";
import moment from "moment";

const planDurationLabel = (item) => {
  if (!item?.subscription_start_date || !item?.subscription_end_date) return "—";
  const days = Math.round((new Date(item.subscription_end_date) - new Date(item.subscription_start_date)) / 86400000);
  if (!Number.isFinite(days) || days <= 0) return "—";
  if (days >= 300) return "Yearly";
  if (days >= 80) return "Quarterly";
  if (days >= 20) return "Monthly";
  return `${days} days`;
};

const addonLabel = (item) => {
  const addons = Array.isArray(item?.addon_details) ? item.addon_details : [];
  if (!addons.length) return "—";
  return addons
    .map((addon) => `${addon.name || "Add-on"}${addon.duration ? ` (${addon.duration})` : ""}`)
    .join(", ");
};

const money = (value) => Number(value || 0).toFixed(2);

export const SaasRetailerList = () => {
  const [listData, setListData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(true);

  // Modals state
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  
  const [rejectionReason, setRejectionReason] = useState("");
  const [docRejects, setDocRejects] = useState({});
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  useEffect(() => {
    getItemList();
    // eslint-disable-next-line
  }, [currentPage]);

  const getItemList = () => {
    let param = { ...pagination, page: currentPage, is_saas: "true" };
    if (pagination.search) {
      param["page"] = 1;
    }
    
    setLoader(true);
    retailerService.getRetailer(param).then((res) => {
      if (res.status) {
        setLoader(false);
        setListData(res.data);
        setPagination({ ...pagination, ...res.pagination });
      } else {
        setLoader(false);
      }
    }).catch(() => {
        setLoader(false);
    });
  };

  const onPageChangeEvent = (e) => {
    setLoader(true);
    setCurrentPage(e.selected + 1);
  };

  const handleVerify = () => {
    let postData = {
      id: selectedRetailer._id,
      retailer_status: "ACTIVE"
    };
    
    setLoader(true);
    retailerService.updateRetailerStatus(postData).then((res) => {
      setLoader(false);
      if (res.status) {
        toast.success("Retailer verified successfully and email sent.");
        setIsVerifyOpen(false);
        setSelectedRetailer(null);
        getItemList();
      } else {
        toast.error(res.message || "Failed to verify retailer");
      }
    });
  };

  const handleReject = () => {
    const docs = Array.isArray(selectedRetailer?.documents) ? selectedRetailer.documents : [];
    const selected = docs.filter((doc) => docRejects[doc.document_type]?.checked);
    if (docs.length > 0 && selected.length === 0) {
      toast.error("Select the document to reject.");
      return;
    }
    if (selected.some((doc) => !String(docRejects[doc.document_type]?.reason || '').trim())) {
      toast.error("Enter a reason for each rejected document.");
      return;
    }
    if (docs.length === 0 && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason.");
      return;
    }

    const rejected_documents = selected.map((doc) => ({
      document_type: doc.document_type,
      reason: String(docRejects[doc.document_type].reason).trim()
    }));
    const rejectionText = rejected_documents.length
      ? rejected_documents.map((doc) => `${String(doc.document_type || '').replace(/_/g, ' ')}: ${doc.reason}`).join('. ')
      : rejectionReason;

    let postData = {
      id: selectedRetailer._id,
      retailer_status: "REJECTED",
      rejection_reason: rejectionText,
      rejected_documents
    };
    
    setLoader(true);
    retailerService.updateRetailerStatus(postData).then((res) => {
      setLoader(false);
      if (res.status) {
        toast.success("Selected documents rejected and email sent.");
        setIsRejectOpen(false);
        setRejectionReason("");
        setDocRejects({});
        setSelectedRetailer(null);
        getItemList();
      } else {
        toast.error(res.message || "Failed to reject retailer");
      }
    });
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title mb-4">SaaS Retailer Approvals</h4>
            <div className="mb-3 row me-1">
              <div className="col-12">
                <div className="input-group w-25">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search by name or email..."
                    onChange={(e) =>
                      setPagination({ ...pagination, search: e.target.value })
                    }
                    onKeyPress={(e) => e.key === "Enter" && getItemList()}
                    onKeyUp={(e) =>
                      e.key === "Backspace" &&
                      pagination.search.length <= 0 &&
                      getItemList()
                    }
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => getItemList()}
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Company Name</th>
                    <th>Email</th>
                    <th>Approval Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!loader ? (
                    listData.length > 0 ? (
                      listData.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {(pagination.page - 1) * pagination.limit +
                              index +
                              1}
                          </td>
                          <td>{item.retailer_name}</td>
                          <td>{item.retailer_email}</td>
                          <td>
                            {item.retailer_status === 'ACTIVE' ? (
                                <Badge bg="success">Approved</Badge>
                            ) : item.retailer_status === 'REJECTED' ? (
                                <Badge bg="danger">Rejected</Badge>
                            ) : (
                                <Badge bg="warning">Pending</Badge>
                            )}
                            {item.documents_resubmitted_at && item.document_verification_status === 'PENDING' && (
                                <div className="mt-1">
                                  <Badge bg="primary">Documents re-uploaded</Badge>
                                </div>
                            )}
                          </td>
                          <td>
                            <button
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => {
                                    setSelectedRetailer(item);
                                    setIsViewOpen(true);
                                    retailerService.getRetailerDetails({ id: item._id }).then((res) => {
                                      if (res.status && Array.isArray(res.data) && res.data[0]) {
                                        setSelectedRetailer((prev) => ({ ...(prev || {}), ...res.data[0] }));
                                      }
                                    });
                                }}
                            >
                                View Details
                            </button>
                            {item.retailer_status !== 'ACTIVE' && (
                                <button
                                    className="btn btn-sm btn-success me-2"
                                    onClick={() => {
                                        setSelectedRetailer(item);
                                        setIsVerifyOpen(true);
                                    }}
                                >
                                    Verify
                                </button>
                            )}
                            {item.retailer_status !== 'REJECTED' && (
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => {
                                        setSelectedRetailer(item);
                                        setRejectionReason("");
                                        setDocRejects({});
                                        setIsRejectOpen(true);
                                    }}
                                >
                                    Reject
                                </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No SaaS Retailers Found
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <Loader />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="pagination-wrap">
                <ReactPaginate
                  previousLabel={"Previous"}
                  nextLabel={"Next"}
                  breakLabel={"..."}
                  breakClassName={"break-me"}
                  pageCount={Math.ceil(
                    pagination.totalRecord / pagination.limit
                  )}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={2}
                  onPageChange={onPageChangeEvent}
                  containerClassName={"pagination"}
                  subContainerClassName={"pages pagination"}
                  activeClassName={"active"}
                  forcePage={currentPage - 1}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verify Modal */}
      <Modal show={isVerifyOpen} onHide={() => setIsVerifyOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Verify SaaS Retailer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to approve and verify <strong>{selectedRetailer?.retailer_name}</strong>?</p>
          <p className="text-muted">An approval email will be sent to {selectedRetailer?.retailer_email}.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsVerifyOpen(false)}>Cancel</Button>
          <Button variant="success" onClick={handleVerify}>Verify</Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={isRejectOpen} onHide={() => setIsRejectOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject SaaS Retailer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Array.isArray(selectedRetailer?.documents) && selectedRetailer.documents.length > 0 ? (
            <>
              <p className="text-muted">Select only the documents to reject. The retailer will upload those documents again.</p>
              {selectedRetailer.documents.map((doc, idx) => (
                <div key={`${doc.document_type}-${idx}`} className="border rounded p-3 mb-3">
                  <Form.Check
                    type="checkbox"
                    id={`reject-${doc.document_type}-${idx}`}
                    label={(doc.document_type || 'document').replace(/_/g, ' ')}
                    checked={!!docRejects[doc.document_type]?.checked}
                    onChange={(e) => setDocRejects((prev) => ({
                      ...prev,
                      [doc.document_type]: { ...(prev[doc.document_type] || {}), checked: e.target.checked }
                    }))}
                    style={{ textTransform: 'capitalize' }}
                  />
                  {docRejects[doc.document_type]?.checked && (
                    <Form.Control
                      className="mt-2"
                      as="textarea"
                      rows={2}
                      value={docRejects[doc.document_type]?.reason || ''}
                      onChange={(e) => setDocRejects((prev) => ({
                        ...prev,
                        [doc.document_type]: { ...(prev[doc.document_type] || {}), reason: e.target.value }
                      }))}
                      placeholder="Reason for rejecting this document"
                    />
                  )}
                </div>
              ))}
            </>
          ) : (
            <Form.Group className="mb-3">
                <Form.Label>Reason for Rejection <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                    as="textarea" 
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please explain why the retailer is being rejected. This will be sent to the retailer via email."
                />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsRejectOpen(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject}>Reject</Button>
        </Modal.Footer>
      </Modal>

      {/* View Details Modal */}
      <Modal show={isViewOpen} onHide={() => setIsViewOpen(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retailer Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRetailer && (
              <div>
                  <h5 className="mb-3 border-bottom pb-2">Company Information</h5>
                  <div className="row mb-3">
                      <div className="col-md-6">
                          <strong>Company Name:</strong> {selectedRetailer.retailer_name}
                      </div>
                      <div className="col-md-6">
                          <strong>Contact Name:</strong> {selectedRetailer.contact_name}
                      </div>
                  </div>
                  <div className="row mb-3">
                      <div className="col-md-6">
                          <strong>Email:</strong> {selectedRetailer.retailer_email}
                      </div>
                      <div className="col-md-6">
                          <strong>Phone:</strong> {selectedRetailer.phone_number}
                      </div>
                  </div>
                  <div className="row mb-3">
                      <div className="col-md-12">
                          <strong>Address:</strong> {selectedRetailer.address}, {selectedRetailer.city}, {selectedRetailer.state}, {selectedRetailer.country}
                      </div>
                  </div>

                  <h5 className="mb-3 border-bottom pb-2 mt-4">Plan Details</h5>
                  <div className="row mb-3">
                      <div className="col-md-6">
                          <strong>Plan:</strong> {selectedRetailer.subscription_plan || "—"}
                      </div>
                      <div className="col-md-6">
                          <strong>Subscription Status:</strong> {selectedRetailer.subscription_status || "—"}
                      </div>
                  </div>
                  <div className="row mb-3">
                      <div className="col-md-6">
                          <strong>Duration:</strong> {planDurationLabel(selectedRetailer)}
                          <div className="text-muted">
                            {selectedRetailer.subscription_start_date ? moment(selectedRetailer.subscription_start_date).format("DD MMM YYYY") : "—"}
                            {" – "}
                            {selectedRetailer.subscription_end_date ? moment(selectedRetailer.subscription_end_date).format("DD MMM YYYY") : "—"}
                          </div>
                      </div>
                      <div className="col-md-6">
                          <strong>Add-ons:</strong> {addonLabel(selectedRetailer)}
                      </div>
                  </div>

                  <h5 className="mb-3 border-bottom pb-2 mt-4">Payments</h5>
                  {Array.isArray(selectedRetailer.saas_payments) && selectedRetailer.saas_payments.length > 0 ? (
                    <div className="table-responsive mb-4">
                      <table className="table table-bordered table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th>Paid on</th>
                            <th>Type</th>
                            <th>Breakdown</th>
                            <th>Subtotal</th>
                            <th>VAT</th>
                            <th>Total</th>
                            <th>AED charged</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRetailer.saas_payments.map((pay) => (
                            <tr key={pay._id || pay.invoice_no}>
                              <td>{pay.invoice_no || "—"}</td>
                              <td>{pay.paid_at ? moment(pay.paid_at).format("DD MMM YYYY") : "—"}</td>
                              <td>{pay.kind_label || "—"}</td>
                              <td style={{ whiteSpace: "normal", minWidth: "180px" }}>
                                {(pay.lines || []).map((line, lineIndex) => (
                                  <div key={lineIndex}>{line.label}: USD {money(line.amount)}</div>
                                ))}
                                {pay.charge_id ? <div className="text-muted">Charge {pay.charge_id}</div> : null}
                              </td>
                              <td>USD {money(pay.subtotal)}</td>
                              <td>USD {money(pay.vat)}</td>
                              <td>USD {money(pay.total)}</td>
                              <td>AED {money(pay.aed_amount)}</td>
                              <td>{pay.status || "Paid"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No payments recorded.</p>
                  )}

                  <h5 className="mb-3 border-bottom pb-2 mt-4">Uploaded Documents</h5>
                  {selectedRetailer.documents_resubmitted_at && selectedRetailer.document_verification_status === 'PENDING' && (
                      <div className="alert alert-info">
                          This retailer uploaded the signup documents again. Please check them.
                      </div>
                  )}
                  {selectedRetailer.documents && selectedRetailer.documents.length > 0 ? (
                      <ul className="list-group">
                          {selectedRetailer.documents.map((doc, idx) => (
                              <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                  <span style={{ textTransform: 'capitalize' }}>
                                    {(doc.document_type || 'document').replace(/_/g, ' ')}
                                    {doc.document_name ? ` — ${doc.document_name}` : ''}
                                    {doc.status === 'REJECTED' ? ' (Rejected)' : doc.status === 'APPROVED' ? ' (Approved)' : ''}
                                  </span>
                                  <span>
                                    <a
                                      href={doc.s3_key ? `${process.env.REACT_APP_API_BASEURL || 'https://dev.world2doorcargo.com/api'}/retailer/document/view?key=${encodeURIComponent(doc.s3_key)}${doc.file_path ? `&url=${encodeURIComponent(doc.file_path)}` : ''}` : (doc.file_path || doc)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-primary me-2"
                                    >
                                        View
                                    </a>
                                    <a
                                      href={doc.s3_key ? `${process.env.REACT_APP_API_BASEURL || 'https://dev.world2doorcargo.com/api'}/retailer/document/view?download=1&key=${encodeURIComponent(doc.s3_key)}${doc.file_path ? `&url=${encodeURIComponent(doc.file_path)}` : ''}` : (doc.file_path || doc)}
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                        Download
                                    </a>
                                  </span>
                              </li>
                          ))}
                      </ul>
                  ) : (
                      <p className="text-muted">No documents uploaded.</p>
                  )}
                  
                  {selectedRetailer.rejection_reason && (
                      <div className="mt-4 alert alert-danger">
                          <strong>Rejection Reason:</strong> {selectedRetailer.rejection_reason}
                      </div>
                  )}
              </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsViewOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};
